import { Injectable } from "@angular/core";
import {
    DataAccess,
    HttpDeleteRequestOptions,
    HttpGetRequestOptions,
    HttpPostRequestOptions,
    HttpPutRequestOptions,
    ResponseMessage,
} from "../../../dataaccess/data.access";
import { Proto } from "../../../pojo/Proto";
import ISODateString = Proto.ISODateString;
import { ValueOf } from "../../../pojo/Util";
import { S25Util } from "../../../util/s25-util";
import { IngestionPoint } from "../../data-ingestion/data.ingestion.service";

export type DBRegistrationConfig = {
    eventId: number;
    start: ISODateString;
    end: ISODateString;
    formId?: number;
    registrantType: "everyone" | "users" | "contacts" | "orgRoles";
    registrants: DBRegistrationConfigObject[];
    lastModUser: string;
    lastModDt: ISODateString;
};

type DBRegistrationConfigObject = {
    eventId: number;
    objectId: number;
};

type DBUserAttendance = {
    attendance: DBAttendeeReservation[];
    canRegister: boolean;
};

export type DBAttendeeReservation = {
    attendeeState: AttendanceState;
    reservationId: number;
    reservationStart: ISODateString;
    registrationSubmissionId?: number;
    registrationSubmissionDate?: ISODateString;
};
export type DBAttendeeHistory = DBAttendeeHistoryEntry[];
export type DBAttendeeHistoryEntry = {
    contId: number;
    rsrvId: number;
    startState: AttendanceState;
    endState: AttendanceState;
    createDt: ISODateString;
    submissionId?: number;
    reservation: { evStartDt: ISODateString };
};

export type DBAttendee = {
    contactId: number;
    contactName: string;
    contactEmail?: string;
    contactPhone?: string;
    contactProfileImage?: string;
    contactInternalId?: string;
    reservations: DBAttendeeReservation[];
    orgRoles: { roleId: number; name: string }[];
};

export const attendanceState = {
    NOT_INTERESTED: 0,
    INTERESTED: 1,
    REGISTERED: 2,
    WAIT_LIST: 3,
    CHECKED_IN: 4,
    NO_SHOW: 5,
    CANCELED: 6,
    PENDING: 7,
    CHECKED_OUT: 8,
    // When adding states, make sure to notify the reports team as they might need to update their code as well
} as const;
export const attendanceStateName = S25Util.reverseObject(attendanceState);
export type AttendanceState = ValueOf<typeof attendanceState>;
export type AttendanceStateName = keyof typeof attendanceState;

export const attendanceDisplayName = {
    [attendanceState.NOT_INTERESTED]: "Not Following",
    [attendanceState.INTERESTED]: "Following",
    [attendanceState.PENDING]: "Pending",
    [attendanceState.REGISTERED]: "Registered",
    [attendanceState.WAIT_LIST]: "Wait List",
    [attendanceState.CHECKED_IN]: "Checked In",
    [attendanceState.NO_SHOW]: "No Show",
    [attendanceState.CANCELED]: "Canceled",
    [attendanceState.CHECKED_OUT]: "Checked Out",
} as const;

export type UpdateAttendanceOptions = {
    occurrences?: number[];
    contactId?: number;
    submissionId?: number;
};

export type SetRegistrationConfig = Omit<DBRegistrationConfig, "lastModUser" | "lastModDt">;

/** Guest identity for public registration */
export type GuestRegistrant = {
    firstName: string;
    familyName: string;
    email: string;
    /** Reservation ids to register for when no registration form is configured */
    occurrenceIds?: number[];
};

@Injectable({ providedIn: "root" })
export class AttendanceService {
    public getUserAttendance(eventId: number, httpOptions?: HttpGetRequestOptions) {
        const url = `/attendance/${eventId}/status.json`;
        const injected = DataAccess.injectCaller(url, "AttendanceService.getUserAttendance");
        return DataAccess.get<DBUserAttendance>(injected, httpOptions);
    }

    public getUserAttendanceForReservation(eventId: number, rsrvId: number, httpOptions?: HttpGetRequestOptions) {
        const url = `/attendance/${eventId}/${rsrvId}/status.json`;
        const injected = DataAccess.injectCaller(url, "AttendanceService.getUserAttendanceForReservation");
        return DataAccess.get<Pick<DBAttendeeReservation, "attendeeState" | "reservationId">>(injected, httpOptions);
    }

    /**
     * Whether the current caller may start registration (logged-in or guest).
     * For guests, backend returns true when registration is open and registrantType is everyone.
     */
    public canRegisterForEvent(eventId: number, httpOptions?: HttpGetRequestOptions) {
        const url = `/attendance/${eventId}/canRegister.json`;
        const injected = DataAccess.injectCaller(url, "AttendanceService.canRegisterForEvent");
        return DataAccess.get<boolean>(injected, httpOptions);
    }

    /**
     * Match or create a contact, create an authless form submission, and email the form link.
     * Server reads formId from the event registration config.
     */
    public async registerGuest(
        eventId: number,
        request: GuestRegistrant,
        httpOptions?: HttpPostRequestOptions,
    ): Promise<ResponseMessage> {
        const url = `/attendance/${eventId}/registerGuest.json`;
        const injected = DataAccess.injectCaller(url, "AttendanceService.registerGuest");
        return DataAccess.post<ResponseMessage>(injected, request, httpOptions);
    }

    public async confirmGuest(token: string, httpOptions?: HttpPostRequestOptions) {
        const url = `/attendance/confirmGuest.json?${DataAccess.params({ token })}`;
        const injected = DataAccess.injectCaller(url, "AttendanceService.confirmGuest");
        return DataAccess.post<ResponseMessage>(injected, {}, httpOptions);
    }

    public getRegistrationConfig(eventId: number, httpOptions?: HttpGetRequestOptions) {
        const url = `/attendance/${eventId}/config.json`;
        const injected = DataAccess.injectCaller(url, "AttendanceService.getRegistrationConfig");
        return DataAccess.get<DBRegistrationConfig>(injected, httpOptions);
    }

    public getRegistrationForm(eventId: number, httpOptions?: HttpGetRequestOptions) {
        const url = `/attendance/${eventId}/registrationForm.json`;
        const injected = DataAccess.injectCaller(url, "AttendanceService.getRegistrationForm");
        return DataAccess.get<IngestionPoint>(injected, httpOptions);
    }

    public async setRegistrationConfig(config: SetRegistrationConfig, httpOptions?: HttpPutRequestOptions) {
        const url = `/attendance/${config.eventId}/config.json`;
        const injected = DataAccess.injectCaller(url, "AttendanceService.setRegistrationConfig");
        return DataAccess.put(injected, config, httpOptions);
    }

    public async deleteRegistrationConfig(eventId: number, httpOptions?: HttpDeleteRequestOptions) {
        const url = `/attendance/${eventId}/config.json`;
        const injected = DataAccess.injectCaller(url, "AttendanceService.deleteRegistrationConfig");
        return DataAccess.delete(injected, httpOptions);
    }

    public async updateAttendance(
        eventId: number,
        state: AttendanceStateName,
        options?: UpdateAttendanceOptions,
        httpOptions?: HttpPostRequestOptions,
    ) {
        const data = options?.occurrences ?? [];
        const contactId = !!options?.contactId ? `&contactId=${options.contactId}` : "";
        const submissionId = !!options?.submissionId ? `&submissionId=${options.submissionId}` : "";

        const url = `/attendance/${eventId}/update.json?state=${attendanceState[state]}${contactId}${submissionId}`;
        const injected = DataAccess.injectCaller(url, "AttendanceService.updateAttendance");
        return DataAccess.post<undefined>(injected, data, httpOptions);
    }

    public async getAttendees(eventId: number, httpOptions?: HttpGetRequestOptions) {
        const url = `/attendance/${eventId}/attendees.json`;
        const injected = DataAccess.injectCaller(url, "AttendanceService.getAttendees");
        return DataAccess.get<DBAttendee[]>(injected, httpOptions);
    }

    public async getAttendeesForReservation(eventId: number, rsrvId: number, httpOptions?: HttpGetRequestOptions) {
        const url = `/attendance/${eventId}/attendees.json?rsrvId=${rsrvId}`;
        const injected = DataAccess.injectCaller(url, "AttendanceService.getAttendeesForReservation");
        return DataAccess.get<DBAttendee[]>(injected, httpOptions);
    }

    public async getAttendeeHistory(eventId: number, contactId: number, httpOptions?: HttpGetRequestOptions) {
        const url = `/attendance/${eventId}/history/${contactId}.json`;
        const injected = DataAccess.injectCaller(url, "AttendanceService.getAttendeeHistory");
        return DataAccess.get<DBAttendeeHistory>(injected, httpOptions);
    }
}
